import {
  Children,
  isValidElement,
  type ReactElement,
  type ReactNode,
  useMemo,
  useState,
} from 'react'
import { ArrowDown, ArrowUp, ArrowUpDown, Search, X } from 'lucide-react'
import { cn } from '@/lib/utils'

interface TableCell {
  content: ReactNode
  text: string
}

interface SortState {
  column: number
  direction: 'asc' | 'desc'
}

export function InteractiveMarkdownTable({ children }: { children: ReactNode }) {
  const table = useMemo(() => parseTable(children), [children])
  const [query, setQuery] = useState('')
  const [filterColumn, setFilterColumn] = useState('all')
  const [sort, setSort] = useState<SortState | null>(null)

  const visibleRows = useMemo(() => {
    const normalizedQuery = normalizeText(query)
    const filtered = normalizedQuery
      ? table.rows.filter((row) => {
          const cells = filterColumn === 'all'
            ? row
            : [row[Number(filterColumn)]].filter((cell): cell is TableCell => Boolean(cell))
          return cells.some((cell) => normalizeText(cell.text).includes(normalizedQuery))
        })
      : table.rows

    if (!sort) return filtered

    return [...filtered].sort((left, right) => {
      const comparison = compareCellValues(
        left[sort.column]?.text || '',
        right[sort.column]?.text || '',
      )
      return sort.direction === 'asc' ? comparison : -comparison
    })
  }, [filterColumn, query, sort, table.rows])

  if (table.headers.length === 0) {
    return (
      <div className="mb-3 overflow-x-auto rounded-xl border border-border">
        <table className="min-w-full">{children}</table>
      </div>
    )
  }

  const toggleSort = (column: number) => {
    setSort((current) => {
      if (!current || current.column !== column) return { column, direction: 'asc' }
      if (current.direction === 'asc') return { column, direction: 'desc' }
      return null
    })
  }

  return (
    <div
      className="not-prose mb-4 overflow-hidden rounded-xl border border-border bg-card"
      data-testid="interactive-markdown-table"
    >
      {table.rows.length > 1 && (
        <div className="flex flex-col gap-2 border-b border-border bg-muted/25 p-2 sm:flex-row sm:items-center">
          <label className="relative min-w-0 flex-1">
            <span className="sr-only">Buscar na tabela</span>
            <Search
              aria-hidden="true"
              className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-faint-foreground"
            />
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Buscar na tabela…"
              className="h-8 w-full rounded-lg border border-input bg-background pl-8 pr-8 text-xs text-foreground outline-none transition focus:border-agent/55 focus:ring-[3px] focus:ring-brand/10"
            />
            {query && (
              <button
                type="button"
                aria-label="Limpar busca"
                onClick={() => setQuery('')}
                className="absolute right-1.5 top-1/2 flex size-5 -translate-y-1/2 items-center justify-center rounded text-faint-foreground hover:bg-accent hover:text-foreground"
              >
                <X aria-hidden="true" className="size-3" />
              </button>
            )}
          </label>
          <label className="flex shrink-0 items-center gap-2 text-[11px] text-muted-foreground">
            <span>Filtrar por</span>
            <select
              value={filterColumn}
              onChange={(event) => setFilterColumn(event.target.value)}
              className="h-8 max-w-48 rounded-lg border border-input bg-background px-2 text-xs text-foreground outline-none focus:border-agent/55 focus:ring-[3px] focus:ring-brand/10"
            >
              <option value="all">Todas as colunas</option>
              {table.headers.map((header, index) => (
                <option key={`${header.text}-${index}`} value={index}>{header.text}</option>
              ))}
            </select>
          </label>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="min-w-full border-collapse text-left text-sm">
          <thead className="bg-muted/45">
            <tr>
              {table.headers.map((header, index) => {
                const activeSort = sort?.column === index ? sort.direction : null
                const SortIcon = activeSort === 'asc'
                  ? ArrowUp
                  : activeSort === 'desc'
                    ? ArrowDown
                    : ArrowUpDown

                return (
                  <th
                    key={`${header.text}-${index}`}
                    scope="col"
                    aria-sort={activeSort === 'asc' ? 'ascending' : activeSort === 'desc' ? 'descending' : 'none'}
                    className="whitespace-nowrap border-b border-r border-border p-0 font-semibold text-foreground last:border-r-0"
                  >
                    <button
                      type="button"
                      onClick={() => toggleSort(index)}
                      className={cn(
                        'flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left transition hover:bg-accent/70',
                        activeSort && 'text-agent',
                      )}
                    >
                      <span>{header.content}</span>
                      <SortIcon aria-hidden="true" className="size-3.5 shrink-0 opacity-60" />
                    </button>
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row, rowIndex) => (
              <tr key={`${row.map((cell) => cell.text).join('|')}-${rowIndex}`} className="border-b border-border last:border-b-0 hover:bg-muted/20">
                {row.map((cell, cellIndex) => (
                  <td key={`${cell.text}-${cellIndex}`} className="border-r border-border px-3 py-2.5 align-top text-foreground last:border-r-0">
                    {cell.content}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {visibleRows.length === 0 && (
        <div className="border-t border-border px-4 py-8 text-center text-xs text-muted-foreground">
          Nenhum resultado encontrado para esta busca.
        </div>
      )}
      {table.rows.length > 1 && (
        <div className="border-t border-border bg-muted/20 px-3 py-1.5 text-[10.5px] tabular-nums text-faint-foreground">
          {visibleRows.length} de {table.rows.length} {table.rows.length === 1 ? 'linha' : 'linhas'}
        </div>
      )}
    </div>
  )
}

function parseTable(children: ReactNode): { headers: TableCell[]; rows: TableCell[][] } {
  const sections = Children.toArray(children).filter(isValidElement)
  const head = sections.find((section) => section.type === 'thead')
  const body = sections.find((section) => section.type === 'tbody')
  const headerRow = head ? childElements(head).find((row) => row.type === 'tr') : undefined
  const bodyRows = body ? childElements(body).filter((row) => row.type === 'tr') : []

  return {
    headers: headerRow ? childElements(headerRow).map(toCell) : [],
    rows: bodyRows.map((row) => childElements(row).map(toCell)),
  }
}

function childElements(element: ReactElement): ReactElement[] {
  const props = element.props as { children?: ReactNode }
  return Children.toArray(props.children).filter(isValidElement)
}

function toCell(element: ReactElement): TableCell {
  const content = (element.props as { children?: ReactNode }).children
  return { content, text: nodeText(content).trim() }
}

function nodeText(node: ReactNode): string {
  if (typeof node === 'string' || typeof node === 'number') return String(node)
  if (Array.isArray(node)) return node.map(nodeText).join(' ')
  if (isValidElement(node)) return nodeText((node.props as { children?: ReactNode }).children)
  return ''
}

function normalizeText(value: string): string {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase('pt-BR')
}

function compareCellValues(left: string, right: string): number {
  const leftNumber = localizedNumber(left)
  const rightNumber = localizedNumber(right)
  if (leftNumber !== null && rightNumber !== null) return leftNumber - rightNumber

  return left.localeCompare(right, 'pt-BR', { numeric: true, sensitivity: 'base' })
}

function localizedNumber(value: string): number | null {
  const compact = value.replace(/[^\d,.-]/g, '')
  if (!compact || !/\d/.test(compact)) return null

  const comma = compact.lastIndexOf(',')
  const dot = compact.lastIndexOf('.')
  let normalized = compact

  if (comma >= 0 && dot >= 0) {
    const decimal = comma > dot ? ',' : '.'
    const thousands = decimal === ',' ? /\./g : /,/g
    normalized = compact.replace(thousands, '').replace(decimal, '.')
  } else if (comma >= 0) {
    const decimals = compact.length - comma - 1
    normalized = decimals === 3 ? compact.replace(/,/g, '') : compact.replace(',', '.')
  } else if (dot >= 0) {
    const decimals = compact.length - dot - 1
    normalized = decimals === 3 ? compact.replace(/\./g, '') : compact
  }

  const parsed = Number(normalized)
  return Number.isFinite(parsed) ? parsed : null
}
